import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Progress } from './ui/progress';
import { Alert, AlertDescription } from './ui/alert';
import { Checkbox } from './ui/checkbox';
import ComuneAutocomplete from './ComuneAutocomplete';
import DocumentScanner from './DocumentScanner';
import { 
  Home, Calendar, Users, FileText, CheckCircle, 
  AlertTriangle, ChevronRight, User, MapPin, 
  CreditCard, Globe, Building, Loader2, ChevronDown, Info, Shield,
  Phone, Mail, PartyPopper, MessageCircle, UserPlus, X, Send, Clock,
  Camera, ScanLine
} from 'lucide-react';
import '../i18n';
import { SUPPORTED_LANGUAGES, detectLanguageFromNationality, detectLanguageFromPhone } from '../i18n';
import { API } from '../utils/config';
import { isGuestTypeDisabled, validateGuestTypeSelection, validateAllGuestTypes } from '../utils/guestTypeRules';

// Searchable Country Dropdown Component
const SearchableCountrySelect = ({ value, onChange, placeholder, className, testId, countries: countriesProp }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [dynamicCountries, setDynamicCountries] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Load countries from API on first open (or use prop)
  useEffect(() => {
    if (countriesProp && countriesProp.length > 0) {
      setDynamicCountries(countriesProp);
      setLoaded(true);
      return;
    }
    const load = async () => {
      try {
        const r = await fetch(`${API}/api/locations/countries/all`);
        if (r.ok) {
          const data = await r.json();
          setDynamicCountries(data.countries || []);
        }
      } catch (e) { console.error('Error loading countries', e); }
      setLoaded(true);
    };
    load();
  }, [countriesProp]);

  const allCountries = loaded ? dynamicCountries : COUNTRIES_FALLBACK;

  const filteredCountries = allCountries.filter(country =>
    country.toLowerCase().includes(search.toLowerCase())
  );

  const searchTrimmed = search.trim();
  const exactMatch = searchTrimmed && allCountries.some(c => c.toLowerCase() === searchTrimmed.toLowerCase());
  const showAddNew = searchTrimmed.length >= 2 && !exactMatch && filteredCountries.length === 0;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (country) => {
    onChange(country);
    setSearch('');
    setIsOpen(false);
  };

  const handleAddCustom = async () => {
    if (!searchTrimmed) return;
    try {
      await fetch(`${API}/api/locations/custom/public`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: searchTrimmed })
      });
    } catch (e) { /* silent */ }
    setDynamicCountries(prev => [...prev, searchTrimmed].sort());
    handleSelect(searchTrimmed);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div 
        className={`flex items-center h-11 sm:h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base cursor-pointer ${className || ''}`}
        onClick={() => {
          setIsOpen(true);
          setTimeout(() => inputRef.current?.focus(), 100);
        }}
        data-testid={testId}
      >
        {isOpen ? (
          <input
            ref={inputRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={placeholder || "Cerca..."}
            className="flex-1 outline-none bg-transparent"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span className={value ? 'text-foreground' : 'text-muted-foreground'}>
            {value || placeholder || "Seleziona..."}
          </span>
        )}
        <ChevronDown className="h-4 w-4 opacity-50 ml-2" />
      </div>
      
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 max-h-60 overflow-auto bg-white border rounded-md shadow-lg">
          {filteredCountries.length > 0 ? (
            filteredCountries.map((country) => (
              <div
                key={country}
                className={`px-3 py-3 cursor-pointer hover:bg-gray-100 active:bg-gray-200 ${value === country ? 'bg-blue-50 text-blue-700' : ''}`}
                onClick={() => handleSelect(country)}
              >
                {country}
              </div>
            ))
          ) : !showAddNew ? (
            <div className="px-3 py-3 text-gray-500 text-center">Nessun risultato</div>
          ) : null}
          {showAddNew && (
            <div
              className="px-3 py-3 cursor-pointer hover:bg-green-50 text-green-700 border-t border-gray-200 flex items-center gap-2"
              onClick={handleAddCustom}
              data-testid="add-custom-country"
            >
              <UserPlus className="h-4 w-4" />
              <span>Aggiungi "<strong>{searchTrimmed}</strong>"</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Fallback countries list (used before API loads)
const COUNTRIES_FALLBACK = [
  'Italia', 'Francia', 'Germania', 'Spagna', 'Regno Unito', 'Portogallo',
  'Svizzera', 'Austria', 'Belgio', 'Paesi Bassi', 'Polonia', 'Romania',
  'Bulgaria', 'Grecia', 'Croazia', 'Slovenia', 'Svezia', 'Norvegia',
  'Danimarca', 'Finlandia', 'Irlanda', 'Repubblica Ceca', 'Ungheria',
  'Russia', 'Ucraina', 'Turchia', 'Stati Uniti', 'Canada', 'Brasile',
  'Argentina', 'Cina', 'Giappone', 'India', 'Australia', 'Altro'
];


const ITALIAN_CITIZENSHIPS = ['italiana', 'italian', 'italy', 'italia'];

// Codice Fiscale validation utilities
const CF_MONTHS = { A: 1, B: 2, C: 3, D: 4, E: 5, H: 6, L: 7, M: 8, P: 9, R: 10, S: 11, T: 12 };

const validateCodiceFiscale = (cf, firstName, lastName, birthDate, gender) => {
  if (!cf || cf.length !== 16) return { valid: false, error: 'codice_fiscale_length' };
  
  const cfUpper = cf.toUpperCase();
  
  // Extract data from CF
  const cfBirthYear = parseInt(cfUpper.substring(6, 8));
  const cfBirthMonthLetter = cfUpper.charAt(8);
  const cfBirthMonth = CF_MONTHS[cfBirthMonthLetter];
  let cfBirthDay = parseInt(cfUpper.substring(9, 11));
  const cfGender = cfBirthDay > 40 ? 'female' : 'male';
  if (cfBirthDay > 40) cfBirthDay -= 40;
  
  // Parse input birth date
  if (!birthDate) return { valid: false, error: 'birth_date_required' };
  const inputDate = new Date(birthDate);
  const inputYear = inputDate.getFullYear() % 100;
  const inputMonth = inputDate.getMonth() + 1;
  const inputDay = inputDate.getDate();
  
  // Validate birth date
  if (cfBirthYear !== inputYear) {
    return { valid: false, error: 'birth_year_mismatch' };
  }
  if (cfBirthMonth !== inputMonth) {
    return { valid: false, error: 'birth_month_mismatch' };
  }
  if (cfBirthDay !== inputDay) {
    return { valid: false, error: 'birth_day_mismatch' };
  }
  
  // Validate gender - form uses 'male'/'female'
  const inputGender = gender?.toLowerCase();
  if (inputGender && inputGender !== cfGender) {
    return { valid: false, error: 'gender_mismatch' };
  }
  
  return { valid: true, error: null };
};

// Campi PERSONALI sempre obbligatori per Questura/ISTAT (la residenza è opzionale,
// auto-risanata lato backend). Il DOCUMENTO è condizionale (vedi isDocumentRequiredFor).
const PERSONAL_REQUIRED_FIELDS = [
  'first_name', 'last_name', 'gender', 'guest_type', 'birth_date',
  'birth_place', 'birth_country', 'citizenship'
];
// Ordine dei campi per lo scroll al primo errore.
const FORM_FIELD_ORDER = [
  ...PERSONAL_REQUIRED_FIELDS,
  'birth_province',
  'document_type', 'document_number', 'document_issue_country', 'document_issue_place'
];
// Tipi "capo": documento SEMPRE obbligatorio (anche se minore).
const LEADER_GUEST_TYPES = ['single', 'family_head', 'group_leader'];

// Età in anni dalla data di nascita (null se non determinabile).
const guestAgeYears = (birthDate) => {
  if (!birthDate) return null;
  const bd = new Date(birthDate);
  if (isNaN(bd.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - bd.getFullYear();
  const m = now.getMonth() - bd.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < bd.getDate())) age -= 1;
  return age;
};

// Documento obbligatorio? SEMPRE per capo/capogruppo/singolo; per familiari/membri
// gruppo solo se ≥14 anni (i minori <14 non-capo sono ESENTI, come per
// Questura/AlloggiatiWeb e validate_guest_for_questura nel backend).
const isDocumentRequiredFor = (data) => {
  const type = (data.guest_type || '').toLowerCase();
  if (LEADER_GUEST_TYPES.includes(type)) return true;
  const age = guestAgeYears(data.birth_date);
  return age === null || age >= 14; // età ignota → richiedi (prudenza)
};

// Normalizzatori per il confronto duplicati (allineati a GuestVerificationModal).
const _normName = (s) => (s || '').toString().trim().toLowerCase().replace(/\s+/g, ' ');
const _normDocNum = (s) => (s || '').toString().replace(/[\s.\-/]/g, '').toUpperCase();

export default function GuestCheckin() {
  const { token: pathToken } = useParams();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  
  // Get token from path parameter OR query string
  const token = pathToken || searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [checkinData, setCheckinData] = useState(null);
  const [currentGuestIndex, setCurrentGuestIndex] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // REMOVED: childrenCount - minori ora inseriti come ospiti normali
  const [languageSelected, setLanguageSelected] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [showLanguageSelector, setShowLanguageSelector] = useState(false);
  const [showCompletionScreen, setShowCompletionScreen] = useState(false);
  const [capacityAlert, setCapacityAlert] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [cfValidationError, setCfValidationError] = useState('');
  
  // Privacy consent (on main page) — marketing implicito nei termini HW Smart
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [finalSubmitError, setFinalSubmitError] = useState('');
  const [incompleteGuests, setIncompleteGuests] = useState([]);
  const [submissionResults, setSubmissionResults] = useState(null);
  
  // Document Scanner state
  const [showDocumentScanner, setShowDocumentScanner] = useState(false);
  const [showResidenceFields, setShowResidenceFields] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    gender: '',
    guest_type: '',
    birth_date: '',
    birth_place: '',
    birth_province: '',
    birth_country: 'Italia',
    citizenship: '',
    codice_fiscale: '',
    residence_address: '',
    residence_city: '',
    residence_province: '',
    residence_postal_code: '',
    residence_country: 'Italia',
    document_type: 'id_card',
    document_number: '',
    document_issuer: '',
    document_issue_place: '',
    document_issue_country: 'Italia',
    document_issue_date: '',
    document_expiry_date: '',
    is_main_guest: false,
    guest_index: 1,
    arrival_date: '',
    departure_date: ''
  });

  // Detect language on load only if user hasn't selected one
  useEffect(() => {
    const langParam = searchParams.get('lang');
    if (langParam && SUPPORTED_LANGUAGES.find(l => l.code === langParam) && !languageSelected) {
      i18n.changeLanguage(langParam);
      setSelectedLanguage(langParam);
      setLanguageSelected(true);
    }
  }, [searchParams, languageSelected]);

  // Reset all state when token changes (new link)
  useEffect(() => {
    // Clear any cached language preference for fresh start
    localStorage.removeItem('i18nextLng');
    
    // Reset all state for new token
    setLanguageSelected(false);
    setSelectedLanguage(null);
    setShowForm(false);
    setShowCompletionScreen(false);
    setCurrentGuestIndex(null);
    setError('');
    setSuccess('');
    setValidationErrors({});
    setCfValidationError('');
    setPrivacyAccepted(false);
    setFinalSubmitError('');
    setIncompleteGuests([]);
    setCheckinData(null);
    setLoading(true);
    
    // Fetch fresh data for this token (with cache busting)
    const fetchFreshData = async () => {
      try {
        const response = await axios.get(`${API}/api/guest-checkin/${token}?_t=${Date.now()}`);
        setCheckinData(response.data);
      } catch (err) {
        if (err.response?.status === 404) {
          setError(t('checkin.error.invalid_link'));
        } else if (err.response?.status === 410) {
          setError(t('checkin.error.expired_link'));
        } else {
          setError(t('checkin.error.generic'));
        }
      } finally {
        setLoading(false);
      }
    };
    
    if (token) {
      fetchFreshData();
    }
  }, [token]);

  // Auto-detect language from guest data ONLY if user hasn't manually selected one
  useEffect(() => {
    if (checkinData && !languageSelected && !searchParams.get('lang')) {
      const phoneLang = detectLanguageFromPhone(checkinData.guest_phone);
      const nationalityLang = detectLanguageFromNationality(checkinData.guest_nationality);
      
      const detectedLang = phoneLang || nationalityLang;
      if (detectedLang && i18n.language !== detectedLang) {
        // Don't auto-switch if language was already selected by user
        // This will show the language selection screen instead
      }
    }
  }, [checkinData, searchParams, languageSelected]);

  const fetchCheckinData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/guest-checkin/${token}`);
      setCheckinData(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError(t('checkin.error.invalid_link'));
      } else if (err.response?.status === 410) {
        setError(t('checkin.error.expired_link'));
      } else {
        setError(t('checkin.error.generic'));
      }
    } finally {
      setLoading(false);
    }
  };

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    setSelectedLanguage(langCode);
    setShowLanguageSelector(false);
  };

  const selectInitialLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    setSelectedLanguage(langCode);
    setLanguageSelected(true);
  };

  const isItalianCitizen = (citizenship) => {
    if (!citizenship) return false;
    return ITALIAN_CITIZENSHIPS.includes(citizenship.toLowerCase().trim());
  };

  // Check if current guest is the last one
  const isLastGuest = () => {
    if (!checkinData) return true;
    const documentsRequired = checkinData.documents_required || checkinData.guests_count || 1;
    // currentGuestIndex starts at 1, so last guest is when currentGuestIndex equals documentsRequired
    console.log('isLastGuest check:', { currentGuestIndex, documentsRequired, isLast: currentGuestIndex >= documentsRequired });
    return currentGuestIndex >= documentsRequired;
  };

  // Cerca un ospite GIÀ inserito che coincide col candidato su TUTTI E 4 i dati:
  // nome + cognome + data di nascita + numero documento (esclude lo slot corrente).
  const findDuplicateGuestLocal = (cand) => {
    const cDoc = _normDocNum(cand.document_number);
    const cFn = _normName(cand.first_name);
    const cLn = _normName(cand.last_name);
    const cBd = (cand.birth_date || '').toString().trim();
    if (!cDoc || !cFn || !cLn || !cBd) return null;
    const list = (checkinData && checkinData.documents) || [];
    return list.find((g) => (
      g.guest_index !== currentGuestIndex &&
      _normDocNum(g.document_number) === cDoc &&
      _normName(g.first_name) === cFn &&
      _normName(g.last_name) === cLn &&
      (g.birth_date || '').toString().trim() === cBd
    )) || null;
  };

  // Rileva un nome di stato estero nel testo del luogo di nascita (dato incoerente con Paese=Italia)
  const FOREIGN_PLACE_HINTS = ['ARGENTINA', 'ARG', 'TAIWAN', 'BRASILE', 'BRAZIL', 'USA', 'STATI UNITI',
    'URUGUAY', 'VENEZUELA', 'CILE', 'CHILE', 'PERU', 'MESSICO', 'MEXICO', 'COLOMBIA', 'FRANCIA', 'FRANCE',
    'GERMANIA', 'GERMANY', 'SPAGNA', 'SPAIN', 'REGNO UNITO', 'ENGLAND', 'SVIZZERA', 'BELGIO', 'BELGIUM',
    'ROMANIA', 'ALBANIA', 'UCRAINA', 'UKRAINE', 'RUSSIA', 'CINA', 'CHINA', 'INDIA', 'MAROCCO', 'MOROCCO'];
  const foreignCountryInPlace = (place) => {
    const p = ` ${String(place || '').toUpperCase().replace(/[(),.']/g, ' ').replace(/\s+/g, ' ')} `;
    if (!p.trim()) return '';
    return FOREIGN_PLACE_HINTS.find(n => n.length >= 3 && p.includes(` ${n} `)) || '';
  };

  const validateForm = () => {
    const errors = {};
    const missingLabels = [];

    // Contesto regole tipologia ospite: totale ospiti + tipi degli ALTRI ospiti già inseriti.
    const _gtTotal = Math.max(checkinData?.documents_required || checkinData?.guests_count || 1, currentGuestIndex);
    const _gtOthers = (checkinData?.documents || [])
      .filter(d => d.guest_index !== currentGuestIndex)
      .map(d => d.guest_type);

    // Campi PERSONALI sempre obbligatori
    PERSONAL_REQUIRED_FIELDS.forEach(field => {
      if (!formData[field] || formData[field].toString().trim() === '') {
        errors[field] = true;
        missingLabels.push(t(`checkin.form.${field}`));
      }
    });

    if ((formData.birth_country === 'Italia' || formData.birth_country?.toLowerCase().includes('ital')) && !formData.birth_province?.trim()) {
      errors.birth_province = true;
      missingLabels.push(t('checkin.form.birth_province'));
    }

    // Incoerenza: Paese di nascita "Italia" ma il luogo contiene uno stato estero
    if (formData.birth_country === 'Italia' || formData.birth_country?.toLowerCase().includes('ital')) {
      const hint = foreignCountryInPlace(formData.birth_place);
      if (hint) {
        errors.birth_country = true;
        missingLabels.push(`${t('checkin.form.birth_country')}: "${formData.birth_place}" → ${hint}?`);
      }
    }

    // DOCUMENTO: obbligatorio solo per capo/capogruppo/singolo o ospiti ≥14 anni.
    if (isDocumentRequiredFor(formData)) {
      if (!formData.document_type || !formData.document_type.toString().trim()) {
        errors.document_type = true;
        missingLabels.push(t('checkin.form.document_type'));
      }
      if (!formData.document_number || !formData.document_number.toString().trim()) {
        errors.document_number = true;
        missingLabels.push(t('checkin.form.document_number'));
      }
      // Luogo di rilascio: basta PAESE oppure LUOGO (almeno uno), non entrambi.
      const hasIssueCountry = formData.document_issue_country && formData.document_issue_country.toString().trim();
      const hasIssuePlace = formData.document_issue_place && formData.document_issue_place.toString().trim();
      if (!hasIssueCountry && !hasIssuePlace) {
        errors.document_issue_country = true;
        errors.document_issue_place = true;
        missingLabels.push(t('checkin.form.document_issue_place'));
      }
    }

    // Validate Codice Fiscale for Italian citizens
    if (isItalianCitizen(formData.citizenship) && formData.codice_fiscale) {
      const cfResult = validateCodiceFiscale(
        formData.codice_fiscale,
        formData.first_name,
        formData.last_name,
        formData.birth_date,
        formData.gender
      );
      if (!cfResult.valid) {
        errors.codice_fiscale = true;
        setCfValidationError(cfResult.error);
      } else {
        setCfValidationError('');
      }
    }
    
    // Date non valide: data di nascita e data di rilascio NON possono essere nel futuro
    // (la scadenza può esserlo). Popup nella lingua dell'ospite + blocco salvataggio.
    const _now = new Date();
    const _today = `${_now.getFullYear()}-${String(_now.getMonth() + 1).padStart(2, '0')}-${String(_now.getDate()).padStart(2, '0')}`;
    let futureDateMsg = '';
    if (formData.birth_date && formData.birth_date > _today) {
      errors.birth_date = true;
      futureDateMsg = t('checkin.error.future_birth_date');
    }
    if (formData.document_issue_date && formData.document_issue_date > _today) {
      errors.document_issue_date = true;
      if (!futureDateMsg) futureDateMsg = t('checkin.error.future_issue_date');
    }

    // Ospite DUPLICATO (stesso nome+cognome+data nascita+n° documento di un altro ospite)
    const dup = findDuplicateGuestLocal(formData);
    if (dup) errors.__duplicate = true;

    // Regola tipologia ospite (1 ospite -> Singolo; più ospiti -> un solo capo)
    let guestTypeMsg = '';
    const _gtErr = validateGuestTypeSelection(formData.guest_type, _gtTotal, _gtOthers);
    if (_gtErr === 'must_be_single') {
      errors.guest_type = true;
      guestTypeMsg = t('checkin.error.guest_type_single_required');
    } else if (_gtErr === 'single_not_allowed' || _gtErr === 'multiple_heads') {
      errors.guest_type = true;
      guestTypeMsg = t('checkin.error.guest_type_one_head');
    } else if (_gtTotal > 1 && isLastGuest()
      && validateAllGuestTypes([..._gtOthers, formData.guest_type], _gtTotal) === 'no_head') {
      errors.guest_type = true;
      guestTypeMsg = t('checkin.error.guest_type_one_head');
    }

    // Messaggi (priorità: date future > duplicato > tipologia ospite > campi mancanti)
    if (futureDateMsg) {
      toast.error(futureDateMsg, { duration: 6000 });
    } else if (dup) {
      const dupName = `${dup.first_name || ''} ${dup.last_name || ''}`.trim();
      toast.error(t('checkin.error.duplicate_guest', { name: dupName }), { duration: 7000 });
    } else if (guestTypeMsg) {
      toast.error(guestTypeMsg, { duration: 7000 });
    } else if (missingLabels.length > 0) {
      toast.error(t('checkin.error.missing_fields', { fields: missingLabels.join(', ') }), { duration: 7000 });
    }

    setValidationErrors(errors);
    // UX: porta l'utente sul PRIMO campo mancante (anche se fuori schermo),
    // così non resta bloccato senza capire quale campo è in rosso
    if (Object.keys(errors).length > 0) {
      const firstErrorField = FORM_FIELD_ORDER.find(f => errors[f]) || Object.keys(errors).find(k => k !== '__duplicate');
      if (firstErrorField) {
        setTimeout(() => {
          const el = document.getElementById(firstErrorField);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            try { el.focus({ preventScroll: true }); } catch (e) { /* noop */ }
          }
        }, 100);
      }
    }
    return Object.keys(errors).length === 0;
  };

  const startAddGuest = (guestIndex, isMain = false) => {
    setCurrentGuestIndex(guestIndex);
    // Totale ospiti della prenotazione: con 1 solo ospite la tipologia è SEMPRE "Singolo".
    const _totalGuests = Math.max(checkinData?.documents_required || checkinData?.guests_count || 1, guestIndex);
    
    // Check if there's an existing document for this guest
    const existingDoc = checkinData?.documents?.find(d => d.guest_index === guestIndex);
    
    if (existingDoc) {
      // Pre-populate form with existing data
      setFormData({
        first_name: existingDoc.first_name || '',
        last_name: existingDoc.last_name || '',
        gender: existingDoc.gender || '',
        guest_type: _totalGuests <= 1 ? 'single' : (existingDoc.guest_type || (isMain ? 'family_head' : '')),
        birth_date: existingDoc.birth_date ? existingDoc.birth_date.split('T')[0] : '',
        birth_place: existingDoc.birth_place || '',
        birth_province: existingDoc.birth_province || '',
        birth_country: existingDoc.birth_country || 'Italia',
        citizenship: existingDoc.citizenship || '',
        codice_fiscale: existingDoc.codice_fiscale || '',
        residence_address: existingDoc.residence_address || '',
        residence_city: existingDoc.residence_city || '',
        residence_province: existingDoc.residence_province || '',
        residence_postal_code: existingDoc.residence_postal_code || '',
        residence_country: existingDoc.residence_country || 'Italia',
        document_type: existingDoc.document_type || 'id_card',
        document_number: existingDoc.document_number || '',
        document_issuer: existingDoc.document_issuer || '',
        document_issue_place: existingDoc.document_issue_place || '',
        document_issue_country: existingDoc.document_issue_country || 'Italia',
        document_issue_date: existingDoc.document_issue_date ? existingDoc.document_issue_date.split('T')[0] : '',
        document_expiry_date: existingDoc.document_expiry_date ? existingDoc.document_expiry_date.split('T')[0] : '',
        is_main_guest: isMain,
        guest_index: guestIndex,
        arrival_date: existingDoc.arrival_date ? existingDoc.arrival_date.split('T')[0] : '',
        departure_date: existingDoc.departure_date ? existingDoc.departure_date.split('T')[0] : ''
      });
      // Auto-expand residence if data already exists
      if (existingDoc.residence_city || existingDoc.residence_address || existingDoc.residence_province) {
        setShowResidenceFields(true);
      } else {
        setShowResidenceFields(false);
      }
    } else {
      // Start with empty form for new entry
      setFormData({
        first_name: '',
        last_name: '',
        gender: '',
        guest_type: _totalGuests <= 1 ? 'single' : (isMain ? 'family_head' : ''),
        birth_date: '',
        birth_place: '',
        birth_province: '',
        birth_country: 'Italia',
        citizenship: '',
        codice_fiscale: '',
        residence_address: '',
        residence_city: '',
        residence_province: '',
        residence_postal_code: '',
        residence_country: 'Italia',
        document_type: 'id_card',
        document_number: '',
        document_issuer: '',
        document_issue_place: '',
        document_issue_country: 'Italia',
        document_issue_date: '',
        document_expiry_date: '',
        is_main_guest: isMain,
        guest_index: guestIndex,
        arrival_date: '',
        departure_date: ''
      });
    }
    
    setShowForm(true);
    setError('');
    setSuccess('');
    setValidationErrors({});
    setCfValidationError('');
    setShowCompletionScreen(false);
    
    // Scroll to top of form when opening
    setTimeout(() => {
      const modal = document.querySelector('[data-testid="document-form-modal"]');
      if (modal) {
        modal.scrollTop = 0;
      }
      window.scrollTo(0, 0);
    }, 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all required fields
    if (!validateForm()) {
      setError(t('checkin.error.validation'));
      return;
    }
    
    setSubmitting(true);
    setError('');
    
    try {
      const saveResponse = await axios.post(
        `${API}/api/guest-checkin/${token}/document`,
        formData
      );
      if (saveResponse.data?.guest_capacity_alert?.is_over_capacity) {
        setCapacityAlert(saveResponse.data.guest_capacity_alert);
      }
      
      // Refresh data to get updated counts
      const response = await axios.get(`${API}/api/guest-checkin/${token}`);
      const updatedData = response.data;
      setCheckinData(updatedData);
      
      // Check if there are more guests to add
      const documentsRequired = updatedData.documents_required || 1;
      const documentsSubmitted = updatedData.documents_submitted || 0;
      
      if (documentsSubmitted < documentsRequired) {
        // Find next guest index that hasn't been submitted
        const submittedIndices = (updatedData.documents || []).map(d => d.guest_index);
        let nextGuestIndex = null;
        for (let i = 1; i <= documentsRequired; i++) {
          if (!submittedIndices.includes(i)) {
            nextGuestIndex = i;
            break;
          }
        }
        
        if (nextGuestIndex) {
          // Auto-open form for next guest
          startAddGuest(nextGuestIndex, false);
          return;
        }
      }
      
      // All guests completed - return to main page for privacy/marketing
      setShowForm(false);
      // Don't show completion screen yet - user needs to accept privacy first
    } catch (err) {
      const data = err.response?.data;
      const detail = data?.detail;
      // Ospite duplicato (controllo server-side): mostra il toast tradotto, non l'errore generico
      if (err.response?.status === 409 && detail && typeof detail === 'object' && detail.code === 'duplicate_guest') {
        toast.error(t('checkin.error.duplicate_guest', { name: detail.guest_name || '' }), { duration: 7000 });
        setError('');
      } else if (err.response?.status === 409 && detail && typeof detail === 'object' && detail.code === 'multiple_heads') {
        toast.error(t('checkin.error.guest_type_multiple_heads_server', { name: detail.guest_name || '' }), { duration: 8000 });
        setError('');
      } else {
        const msg = (typeof detail === 'string' ? detail : (detail && detail.message) || data?.message) || t('checkin.error.save_error');
        setError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Handle OCR scanned data
  const handleOcrDataExtracted = (ocrData) => {
    if (!ocrData || !ocrData.success) return;
    
    // Map OCR data to form fields
    const mappedData = {};
    
    // Personal info
    if (ocrData.nome) mappedData.first_name = ocrData.nome;
    if (ocrData.cognome) mappedData.last_name = ocrData.cognome;
    if (ocrData.sesso) mappedData.gender = ocrData.sesso.toLowerCase() === 'm' ? 'male' : 'female';
    if (ocrData.data_nascita) {
      // Convert DD/MM/YYYY to YYYY-MM-DD
      const parts = ocrData.data_nascita.split('/');
      if (parts.length === 3) {
        mappedData.birth_date = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
      }
    }
    if (ocrData.luogo_nascita) mappedData.birth_place = ocrData.luogo_nascita;
    if (ocrData.nazionalita) mappedData.citizenship = ocrData.nazionalita;
    if (ocrData.codice_fiscale) mappedData.codice_fiscale = ocrData.codice_fiscale;
    
    // Document info
    if (ocrData.tipo_documento) {
      const docTypeMap = {
        'carta_identita': 'id_card',
        'passaporto': 'passport',
        'patente': 'driving_license',
        'permesso_soggiorno': 'residence_permit'
      };
      mappedData.document_type = docTypeMap[ocrData.tipo_documento] || 'id_card';
    }
    if (ocrData.numero_documento) mappedData.document_number = ocrData.numero_documento;
    if (ocrData.luogo_rilascio) mappedData.document_issue_place = ocrData.luogo_rilascio;
    if (ocrData.data_rilascio) {
      const parts = ocrData.data_rilascio.split('/');
      if (parts.length === 3) {
        mappedData.document_issue_date = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
      }
    }
    if (ocrData.data_scadenza) {
      const parts = ocrData.data_scadenza.split('/');
      if (parts.length === 3) {
        mappedData.document_expiry_date = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
      }
    }
    
    // Address (if present, from Italian ID)
    if (ocrData.indirizzo) mappedData.residence_address = ocrData.indirizzo;
    
    // Merge with existing form data
    setFormData(prev => ({
      ...prev,
      ...mappedData
    }));
    
    setShowDocumentScanner(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const locale = i18n.language === 'it' ? 'it-IT' : 
                   i18n.language === 'de' ? 'de-DE' : 
                   i18n.language === 'fr' ? 'fr-FR' : 'en-GB';
    return date.toLocaleDateString(locale, { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const getProgressPercentage = () => {
    if (!checkinData) return 0;
    const required = checkinData.documents_required || 1;
    const submitted = checkinData.documents_submitted || 0;
    return Math.round((submitted / required) * 100);
  };

  const getGuestStatus = (guestIndex) => {
    if (!checkinData?.documents) return 'pending';
    const doc = checkinData.documents.find(d => d.guest_index === guestIndex);
    return doc?.status || 'pending';
  };

  const guestSlots = Math.max(
    checkinData?.documents_required || 1,
    ...(checkinData?.documents || []).map(doc => Number(doc.guest_index) || 0)
  );

  const getCurrentLang = () => {
    return SUPPORTED_LANGUAGES.find(l => l.code === i18n.language) || SUPPORTED_LANGUAGES[1];
  };

  const getFieldError = (fieldName) => {
    return validationErrors[fieldName] ? 'border-red-500 bg-red-50' : '';
  };

  // Ogni scheda è già validata prima del salvataggio. La conferma finale verifica
  // soltanto che tutti gli slot siano stati salvati e che la privacy sia accettata.
  const handleFinalSubmit = async () => {
    setFinalSubmitError('');
    setIncompleteGuests([]);
    setSubmitting(true);
    
    // Check if all guests have submitted their documents
    const documentsRequired = checkinData?.documents_required || 1;
    // Verifica solo presenza e stato: non rileggere campi personali oscurati dal riepilogo.
    const incomplete = [];
    for (let i = 1; i <= documentsRequired; i++) {
      const status = getGuestStatus(i);
      if (status !== 'submitted' && status !== 'verified' && status !== 'sent_questura') {
        incomplete.push(i);
      }
    }
    
    if (incomplete.length > 0) {
      setIncompleteGuests(incomplete);
      setFinalSubmitError(t('checkin.final_submit.guests_incomplete'));
      setSubmitting(false);
      return;
    }
    
    // Check privacy acceptance
    if (!privacyAccepted) {
      setFinalSubmitError(t('checkin.final_submit.privacy_required'));
      setSubmitting(false);
      return;
    }
    
    // Completa soltanto la raccolta dati; Questura e ISTAT seguono i flussi separati.
    try {
      const response = await axios.post(`${API}/api/guest-checkin/${token}/complete`, {
        privacy_accepted: true,
        marketing_consent: true
      });
      
      if (response.data.success) {
        // Store submission results for display
        setSubmissionResults({
          questura: response.data.questura,
          tourism: response.data.tourism,
          guests_registered: response.data.guests_registered
        });
        // Show completion screen
        setShowCompletionScreen(true);
      } else {
        setFinalSubmitError(response.data.message || t('checkin.final_submit.error'));
      }
    } catch (err) {
      console.error('Error completing check-in:', err);
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.guests?.length) {
        const first = detail.guests[0];
        setFinalSubmitError(detail.message || t('checkin.final_submit.guests_incomplete'));
        startAddGuest(first.guest_index, first.guest_index === 1);
        const errors = Object.fromEntries((first.missing_fields || []).map(field => [field, true]));
        setTimeout(() => setValidationErrors(errors), 120);
      } else {
        setFinalSubmitError((typeof detail === 'string' ? detail : detail?.message) || t('checkin.final_submit.error'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <img src="/1.png" alt="HWsmart Logo" className="h-16 w-auto mb-6" data-testid="hwsmart-logo" />
            <Loader2 className="h-12 w-12 animate-spin text-blue-600 mb-4" />
            <p className="text-gray-600">{t('checkin.loading')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error && !checkinData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <img src="/1.png" alt="HWsmart Logo" className="h-16 w-auto mb-6" data-testid="hwsmart-logo" />
            <AlertTriangle className="h-16 w-16 text-red-500 mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Oops!</h2>
            <p className="text-gray-600 text-center">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Language Selection Screen (first screen)
  if (!languageSelected && checkinData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-3 sm:p-4">
        <Card className="w-full max-w-md mx-2" data-testid="language-selection-screen">
          <CardContent className="flex flex-col items-center justify-center py-8 sm:py-12 px-4 sm:px-6">
            <img src="/1.png" alt="HWsmart Logo" className="h-16 sm:h-20 w-auto mb-6 sm:mb-8" data-testid="hwsmart-logo" />
            
            <Globe className="h-10 sm:h-12 w-10 sm:w-12 text-blue-600 mb-3 sm:mb-4" />
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 text-center">Welcome / Benvenuto</h2>
            <p className="text-sm sm:text-base text-gray-600 text-center mb-6 sm:mb-8">Select your language / Seleziona la tua lingua</p>
            
            <div className="w-full space-y-2 sm:space-y-3">
              {SUPPORTED_LANGUAGES.map((lang) => (
                <Button
                  key={lang.code}
                  variant="outline"
                  className="w-full py-5 sm:py-6 text-base sm:text-lg flex items-center justify-center gap-3 sm:gap-4 hover:bg-blue-50 hover:border-blue-300 active:bg-blue-100"
                  onClick={() => selectInitialLanguage(lang.code)}
                  data-testid={`lang-select-${lang.code}`}
                >
                  <span className="text-2xl sm:text-3xl">{lang.flag}</span>
                  <span className="font-medium">{lang.name}</span>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isComplete = checkinData?.is_complete;

  // Completion Screen
  if (showCompletionScreen) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 py-8 px-4">
        <div className="max-w-lg mx-auto space-y-6">
          {/* Thank You Card - Logo dentro la card */}
          <Card className="overflow-hidden" data-testid="completion-card">
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-6 sm:p-8 text-white text-center">
              {/* Logo HWsmart */}
              <div className="flex justify-center mb-4">
                <img src="/1.png" alt="HWsmart Logo" className="h-14 sm:h-16 w-auto" data-testid="completion-logo" />
              </div>
              <PartyPopper className="h-12 sm:h-16 w-12 sm:w-16 mx-auto mb-4" />
              <h1 className="text-2xl sm:text-3xl font-bold mb-2">{t('checkin.completion.title')}</h1>
              <p className="text-green-100 text-sm sm:text-base">{t('checkin.completion.message')}</p>
            </div>
            
            <CardContent className="p-4 sm:p-6 space-y-5 sm:space-y-6">
              {/* Thank you message */}
              <div className="text-center">
                <p className="text-gray-700 text-sm sm:text-base">
                  {t('checkin.completion.thanks_booking')} <strong>{checkinData?.property_name}</strong>
                </p>
                <p className="text-green-600 font-semibold mt-2 text-sm sm:text-base">
                  {t('checkin.completion.ready_checkin')}
                </p>
              </div>

              {/* Property Manager Contact */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 text-sm sm:text-base">
                  <User className="h-5 w-5 text-blue-600" />
                  {t('checkin.completion.contact_title')}
                </h3>
                
                {checkinData?.property_manager_name ? (
                  <div className="space-y-3">
                    <p className="font-medium text-gray-900">{checkinData.property_manager_name}</p>
                    {checkinData.property_manager_phone && (
                      <p className="flex items-center gap-2 text-gray-600 text-sm">
                        <Phone className="h-4 w-4" />
                        <a href={`tel:${checkinData.property_manager_phone}`} className="text-blue-600 hover:underline">
                          {checkinData.property_manager_phone}
                        </a>
                      </p>
                    )}
                    {checkinData.property_manager_email && (
                      <p className="flex items-center gap-2 text-gray-600 text-sm">
                        <Mail className="h-4 w-4" />
                        <a href={`mailto:${checkinData.property_manager_email}`} className="text-blue-600 hover:underline">
                          {checkinData.property_manager_email}
                        </a>
                      </p>
                    )}
                    
                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-2 mt-4 pt-3 border-t border-blue-200">
                      {/* Save Contact Button */}
                      <Button
                        variant="outline"
                        className="flex-1 h-11 text-sm bg-white"
                        onClick={() => {
                          const vcard = `BEGIN:VCARD
VERSION:3.0
FN:${checkinData.property_manager_name}
ORG:${t('checkin.completion.responsible')} ${checkinData?.property_name || ''}
TEL:${checkinData.property_manager_phone || ''}
EMAIL:${checkinData.property_manager_email || ''}
END:VCARD`;
                          const blob = new Blob([vcard], { type: 'text/vcard' });
                          const url = URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = `${checkinData.property_manager_name}.vcf`;
                          link.click();
                        }}
                        data-testid="btn-save-contact"
                      >
                        <UserPlus className="h-4 w-4 mr-2" />
                        {t('checkin.completion.save_contact')}
                      </Button>
                      
                      {/* WhatsApp Button */}
                      {checkinData.property_manager_phone && (
                        <Button
                          className="flex-1 h-11 text-sm bg-green-600 hover:bg-green-700"
                          onClick={() => {
                            const phone = checkinData.property_manager_phone.replace(/\D/g, '');
                            const checkinDate = checkinData.check_in_date ? new Date(checkinData.check_in_date).toLocaleDateString() : '';
                            const checkoutDate = checkinData.check_out_date ? new Date(checkinData.check_out_date).toLocaleDateString() : '';
                            const message = encodeURIComponent(
                              t('checkin.completion.whatsapp_message')
                                .replace('{guest_name}', checkinData?.guest_name || '')
                                .replace('{property}', checkinData?.property_name || '')
                                .replace('{checkin}', checkinDate)
                                .replace('{checkout}', checkoutDate)
                            );
                            window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
                          }}
                          data-testid="btn-whatsapp"
                        >
                          <MessageCircle className="h-4 w-4 mr-2" />
                          {t('checkin.completion.write_whatsapp')}
                        </Button>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 italic">{t('checkin.completion.no_contact')}</p>
                )}
              </div>

              {/* See you soon */}
              <p className="text-center text-xl font-semibold text-gray-700">
                {t('checkin.completion.see_you_soon')} 🏖️
              </p>

              {/* Add another guest button */}
              {!isComplete && (
                <Button 
                  className="w-full" 
                  onClick={() => setShowCompletionScreen(false)}
                  data-testid="add-another-guest-btn"
                >
                  {t('checkin.completion.add_another')}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Footer */}
          <div className="text-center text-sm text-gray-500 pb-4">
            <p>{t('checkin.footer.company')}</p>
            <p>{t('checkin.footer.security_notice')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-4 sm:py-8 px-3 sm:px-4">
      <div className="max-w-lg mx-auto space-y-4 sm:space-y-6">
        
        {/* Logo */}
        <div className="flex justify-center">
          <img src="/1.png" alt="HWsmart Logo" className="h-12 sm:h-16 w-auto" data-testid="hwsmart-logo" />
        </div>
        
        {/* Header Card */}
        <Card className="overflow-hidden" data-testid="property-card">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white">
            <div className="flex items-center gap-3 mb-2">
              <Home className="h-8 w-8" />
              <div>
                <h1 className="text-xl font-bold">{checkinData?.property_name}</h1>
                <p className="text-blue-100 text-sm flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {checkinData?.property_address}, {checkinData?.property_city}
                </p>
              </div>
            </div>
          </div>
          
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="text-center">
                <p className="text-xs text-gray-500 uppercase">{t('checkin.property.check_in')}</p>
                <p className="font-semibold text-green-600">
                  {formatDate(checkinData?.check_in)}
                </p>
              </div>
              <ChevronRight className="h-6 w-6 text-gray-300" />
              <div className="text-center">
                <p className="text-xs text-gray-500 uppercase">{t('checkin.property.check_out')}</p>
                <p className="font-semibold text-red-600">
                  {formatDate(checkinData?.check_out)}
                </p>
              </div>
            </div>
            
            <div className="flex items-center justify-center gap-2 text-gray-700">
              <Users className="h-5 w-5" />
              <span className="font-medium">{checkinData?.guests_count} {t('checkin.property.guests')}</span>
            </div>
          </CardContent>
        </Card>

        {/* Progress Card */}
        <Card data-testid="documents-progress-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              {t('checkin.documents.title')}
            </CardTitle>
            <CardDescription>
              {t('checkin.documents.subtitle')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600">{t('checkin.documents.progress')}</span>
                <span className="font-bold text-blue-600">
                  {checkinData?.documents_submitted || 0} / {checkinData?.documents_required || 1}
                </span>
              </div>
              <Progress value={getProgressPercentage()} className="h-3" />
            </div>
            
            {isComplete ? (
              <Alert className="bg-green-50 border-green-200">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <AlertDescription className="text-green-800 ml-2">
                  <strong>{t('checkin.documents.complete_title')}</strong> {t('checkin.documents.complete_message')}
                  <p className="text-sm mt-1 text-green-700">Puoi verificare e modificare i dati se vedi errori.</p>
                </AlertDescription>
              </Alert>
            ) : (checkinData?.documents_submitted || 0) >= (checkinData?.documents_required || 1) ? (
              <Alert className="bg-blue-50 border-blue-200">
                <CheckCircle className="h-5 w-5 text-blue-600" />
                <AlertDescription className="text-blue-800 ml-2">
                  <strong>Documenti già inseriti.</strong> Tutti i documenti per questa prenotazione sono già stati compilati. Puoi verificare e modificare se vedi errori.
                </AlertDescription>
              </Alert>
            ) : (
              <Alert className="bg-amber-50 border-amber-200">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                <AlertDescription className="text-amber-800 ml-2">
                  <strong>{t('checkin.documents.warning_title')}</strong> {t('checkin.documents.warning_message')}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Guest List */}
        <Card data-testid="guest-list-card">
          <CardHeader>
            <CardTitle>{t('checkin.guest_list.title')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {[...Array(guestSlots)].map((_, index) => {
              const guestIndex = index + 1;
              const status = getGuestStatus(guestIndex);
              const isMainGuest = guestIndex === 1;
              const existingDoc = checkinData?.documents?.find(d => d.guest_index === guestIndex);
              const isIncomplete = incompleteGuests.includes(guestIndex);
              
              return (
                <div
                  key={guestIndex}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    status === 'submitted' || status === 'verified'
                      ? 'bg-green-50 border-green-200'
                      : isIncomplete
                        ? 'bg-red-50 border-red-400 animate-pulse'
                        : 'bg-gray-50 border-gray-200 hover:border-blue-300'
                  }`}
                  data-testid={`guest-item-${guestIndex}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${
                        status === 'submitted' || status === 'verified'
                          ? 'bg-green-100'
                          : isIncomplete
                            ? 'bg-red-100'
                            : 'bg-gray-200'
                      }`}>
                        {status === 'submitted' || status === 'verified' ? (
                          <CheckCircle className="h-5 w-5 text-green-600" />
                        ) : isIncomplete ? (
                          <AlertTriangle className="h-5 w-5 text-red-500" />
                        ) : (
                          <User className="h-5 w-5 text-gray-500" />
                        )}
                      </div>
                      <div>
                        <p className={`font-medium ${isIncomplete ? 'text-red-700' : 'text-gray-900'}`}>
                          {existingDoc 
                            ? `${existingDoc.first_name} ${existingDoc.last_name}`
                            : `${t('checkin.guest_list.guest')} ${guestIndex}`}
                          {isMainGuest && (
                            <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                              {t('checkin.guest_list.main_guest')}
                            </span>
                          )}
                        </p>
                        <p className={`text-sm ${isIncomplete ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                          {status === 'submitted' || status === 'verified'
                            ? t('checkin.guest_list.document_submitted')
                            : isIncomplete
                              ? t('checkin.guest_list.document_required')
                              : t('checkin.guest_list.waiting_document')}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant={status === 'submitted' ? 'outline' : isIncomplete ? 'destructive' : 'default'}
                      size="sm"
                      onClick={() => startAddGuest(guestIndex, isMainGuest)}
                      data-testid={`guest-action-btn-${guestIndex}`}
                    >
                      {status === 'submitted' ? t('checkin.guest_list.edit') : t('checkin.guest_list.insert')}
                    </Button>
                  </div>
                </div>
              );
            })}
            <Button
              type="button"
              variant="outline"
              className="w-full border-dashed"
              onClick={() => startAddGuest(guestSlots + 1, false)}
              data-testid="add-extra-guest-button"
            >
              <User className="h-4 w-4 mr-2" />Aggiungi un altro ospite
            </Button>
          </CardContent>
        </Card>

        {/* Privacy and Marketing Section - Always show until final completion */}
        {!showCompletionScreen && (
          <Card data-testid="privacy-section-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-blue-600" />
                {t('checkin.privacy.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Privacy Consent - Required */}
              <div className={`flex items-start gap-3 p-4 rounded-lg border ${!privacyAccepted && finalSubmitError ? 'bg-red-50 border-red-300' : 'bg-gray-50 border-gray-200'}`}>
                <Checkbox
                  id="privacy_main"
                  checked={privacyAccepted}
                  onCheckedChange={(checked) => {
                    setPrivacyAccepted(checked);
                    if (checked) setFinalSubmitError('');
                  }}
                  data-testid="checkbox-privacy-main"
                  className="mt-1"
                />
                <div className="flex-1">
                  <Label htmlFor="privacy_main" className="text-sm font-medium cursor-pointer">
                    {t('checkin.privacy.checkbox_label')} <span className="text-red-500">*</span>
                  </Label>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('checkin.privacy.checkbox_description')}
                  </p>
                  <p className="text-xs text-gray-400 mt-2 italic">
                    {t('checkin.privacy.full_policy_location')}
                  </p>
                </div>
              </div>

              {/* Error message */}
              {finalSubmitError && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{finalSubmitError}</AlertDescription>
                </Alert>
              )}

              {/* Final Submit Button */}
              <Button
                onClick={handleFinalSubmit}
                className="w-full h-12 text-lg bg-green-600 hover:bg-green-700"
                disabled={submitting}
                data-testid="btn-final-submit"
              >
                <Send className="h-5 w-5 mr-2" />
                {t('checkin.final_submit.button')}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Document Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
            <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto my-8" data-testid="document-form-modal">
              <CardHeader className="sticky top-0 bg-white border-b z-10">
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-blue-600" />
                  {t('checkin.form.title')} {currentGuestIndex}
                  {formData.is_main_guest && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                      {t('checkin.guest_list.main_guest')}
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <form onSubmit={handleSubmit}>
                <CardContent className="space-y-5 sm:space-y-6 p-4 sm:p-6 pb-20 sm:pb-20">
                  {error && (
                    <Alert variant="destructive">
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}
                  
                  {/* Progress indicator for multi-guest */}
                  {(checkinData?.documents_required || 1) > 1 && (
                    <div className="bg-blue-50 rounded-lg p-3 sm:p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-blue-700">
                          {t('checkin.guest_list.guest')} {currentGuestIndex} / {checkinData?.documents_required}
                        </span>
                        <span className="text-xs text-blue-600">
                          {Math.round(((checkinData?.documents_submitted || 0) / (checkinData?.documents_required || 1)) * 100)}%
                        </span>
                      </div>
                      <div className="w-full bg-blue-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                          style={{ width: `${((checkinData?.documents_submitted || 0) / (checkinData?.documents_required || 1)) * 100}%` }}
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Anagrafica */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                        <User className="h-4 w-4" /> {t('checkin.form.personal_data')}
                      </h3>
                      
                      {/* Document Scanner Button */}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setShowDocumentScanner(true)}
                        className="flex items-center gap-2 text-purple-600 border-purple-200 hover:bg-purple-50"
                        data-testid="btn-scan-document"
                      >
                        <ScanLine className="h-4 w-4" />
                        <span className="hidden sm:inline">{t('checkin.form.scan_document', 'Scansiona documento')}</span>
                        <span className="sm:hidden">{t('checkin.form.scan', 'Scansiona')}</span>
                      </Button>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div>
                        <Label htmlFor="first_name" className="text-sm">{t('checkin.form.first_name')} *</Label>
                        <Input
                          id="first_name"
                          value={formData.first_name}
                          onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                          className={`h-11 sm:h-10 text-base ${getFieldError('first_name')}`}
                          placeholder=""
                          data-testid="input-first-name"
                        />
                        {validationErrors.first_name && <span className="text-xs text-red-500">{t('checkin.form.required_field')}</span>}
                      </div>
                      <div>
                        <Label htmlFor="last_name" className="text-sm">{t('checkin.form.last_name')} *</Label>
                        <Input
                          id="last_name"
                          value={formData.last_name}
                          onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                          className={`h-11 sm:h-10 text-base ${getFieldError('last_name')}`}
                          placeholder=""
                          data-testid="input-last-name"
                        />
                        {validationErrors.last_name && <span className="text-xs text-red-500">{t('checkin.form.required_field')}</span>}
                      </div>
                    </div>
                    
                    {/* Gender & Guest Type */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div>
                        <Label htmlFor="gender" className="text-sm">{t('checkin.form.gender')} *</Label>
                        <Select 
                          value={formData.gender} 
                          onValueChange={(v) => setFormData({...formData, gender: v})}
                        >
                          <SelectTrigger className={`h-11 sm:h-10 ${getFieldError('gender')}`} data-testid="select-gender">
                            <SelectValue placeholder="..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="male" className="py-3">{t('checkin.form.gender_male')}</SelectItem>
                            <SelectItem value="female" className="py-3">{t('checkin.form.gender_female')}</SelectItem>
                          </SelectContent>
                        </Select>
                        {validationErrors.gender && <span className="text-xs text-red-500">{t('checkin.form.required_field')}</span>}
                      </div>
                      <div>
                        <Label htmlFor="guest_type" className="text-sm">{t('checkin.form.guest_type')} *</Label>
                        <Select 
                          value={formData.guest_type} 
                          onValueChange={(v) => setFormData({...formData, guest_type: v})}
                        >
                          <SelectTrigger className={getFieldError('guest_type')} data-testid="select-guest-type">
                            <SelectValue placeholder="..." />
                          </SelectTrigger>
                          <SelectContent>
                            {(() => {
                              const _t = Math.max(checkinData?.documents_required || checkinData?.guests_count || 1, currentGuestIndex);
                              const _o = (checkinData?.documents || [])
                                .filter(d => d.guest_index !== currentGuestIndex)
                                .map(d => d.guest_type);
                              return (
                                <>
                                  <SelectItem value="single" disabled={isGuestTypeDisabled('single', _t, _o)}>{t('checkin.form.guest_type_single')}</SelectItem>
                                  <SelectItem value="family_head" disabled={isGuestTypeDisabled('family_head', _t, _o)}>{t('checkin.form.guest_type_family_head')}</SelectItem>
                                  <SelectItem value="family_member" disabled={isGuestTypeDisabled('family_member', _t, _o)}>{t('checkin.form.guest_type_family_member')}</SelectItem>
                                  <SelectItem value="group_leader" disabled={isGuestTypeDisabled('group_leader', _t, _o)}>{t('checkin.form.guest_type_group_leader')}</SelectItem>
                                  <SelectItem value="group_member" disabled={isGuestTypeDisabled('group_member', _t, _o)}>{t('checkin.form.guest_type_group_member')}</SelectItem>
                                </>
                              );
                            })()}
                          </SelectContent>
                        </Select>
                        {validationErrors.guest_type && <span className="text-xs text-red-500">{t('checkin.form.required_field')}</span>}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div>
                        <Label htmlFor="birth_date" className="text-sm">{t('checkin.form.birth_date')} *</Label>
                        <Input
                          id="birth_date"
                          type="date"
                          value={formData.birth_date}
                          onChange={(e) => setFormData({...formData, birth_date: e.target.value})}
                          className={`h-11 sm:h-10 ${getFieldError('birth_date')}`}
                          data-testid="input-birth-date"
                        />
                        {validationErrors.birth_date && <span className="text-xs text-red-500">{t('checkin.form.required_field')}</span>}
                      </div>
                      <div>
                        <Label htmlFor="birth_place" className="text-sm">{t('checkin.form.birth_place')} *</Label>
                        {(formData.birth_country === 'Italia' || formData.birth_country?.toLowerCase().includes('ital')) ? (
                          <ComuneAutocomplete
                            id="birth_place"
                            value={formData.birth_place}
                            onChange={(value) => setFormData({...formData, birth_place: value})}
                            onSelect={(comune) => {
                              setFormData({
                                ...formData, 
                                birth_place: comune.nome,
                                birth_province: comune.provincia
                              });
                            }}
                            placeholder={t('checkin.form.search_comune')}
                            error={!!validationErrors.birth_place}
                            allowCustom={true}
                          />
                        ) : (
                          <Input
                            id="birth_place"
                            value={formData.birth_place}
                            onChange={(e) => setFormData({...formData, birth_place: e.target.value})}
                            className={`h-11 sm:h-10 ${getFieldError('birth_place')}`}
                            placeholder=""
                            data-testid="input-birth-place"
                          />
                        )}
                        {validationErrors.birth_place && <span className="text-xs text-red-500">{t('checkin.form.required_field')}</span>}
                      </div>
                      {/* Birth Province - required for Italian births */}
                      {(formData.birth_country === 'Italia' || formData.birth_country?.toLowerCase().includes('ital')) && (
                        <div>
                          <Label htmlFor="birth_province" className="text-sm">{t('checkin.form.birth_province')} *</Label>
                          <Input
                            id="birth_province"
                            value={formData.birth_province}
                            onChange={(e) => setFormData({...formData, birth_province: e.target.value.toUpperCase()})}
                            className={`h-11 sm:h-10 ${getFieldError('birth_province')}`}
                            placeholder="RM"
                            maxLength={2}
                            data-testid="input-birth-province"
                          />
                          {validationErrors.birth_province ? <span className="text-xs text-red-500">{t('checkin.form.required_field')}</span> : <span className="text-xs text-gray-500">{t('checkin.form.province_hint')}</span>}
                        </div>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div>
                        <Label htmlFor="birth_country" className="text-sm">{t('checkin.form.birth_country')} *</Label>
                        <SearchableCountrySelect
                          value={formData.birth_country}
                          onChange={(v) => setFormData({...formData, birth_country: v})}
                          placeholder={t('checkin.form.search_country')}
                          className={getFieldError('birth_country')}
                          testId="select-birth-country"
                        />
                        {validationErrors.birth_country && <span className="text-xs text-red-500">{t('checkin.form.required_field')}</span>}
                      </div>
                      <div>
                        <Label htmlFor="citizenship" className="text-sm">{t('checkin.form.citizenship')} *</Label>
                        <Input
                          id="citizenship"
                          value={formData.citizenship}
                          onChange={(e) => setFormData({...formData, citizenship: e.target.value})}
                          className={getFieldError('citizenship')}
                          placeholder=""
                          data-testid="input-citizenship"
                        />
                        {validationErrors.citizenship && <span className="text-xs text-red-500">{t('checkin.form.required_field')}</span>}
                      </div>
                    </div>
                    
                    {/* Codice Fiscale - only for Italian citizens */}
                    {isItalianCitizen(formData.citizenship) && (
                      <div className={`border-l-4 ${cfValidationError ? 'border-red-500 bg-red-50' : formData.codice_fiscale?.length === 16 && !cfValidationError ? 'border-green-500 bg-green-50' : 'border-blue-500 bg-blue-50'} pl-4 py-2 rounded-r`}>
                        <Label htmlFor="codice_fiscale">{t('checkin.form.codice_fiscale')}</Label>
                        <p className="text-xs text-blue-600 mb-2">{t('checkin.form.codice_fiscale_hint')}</p>
                        <Input
                          id="codice_fiscale"
                          value={formData.codice_fiscale}
                          onChange={(e) => {
                            const newCF = e.target.value.toUpperCase();
                            setFormData({...formData, codice_fiscale: newCF});
                            
                            // Real-time validation
                            if (newCF.length === 16) {
                              const cfResult = validateCodiceFiscale(
                                newCF,
                                formData.first_name,
                                formData.last_name,
                                formData.birth_date,
                                formData.gender
                              );
                              setCfValidationError(cfResult.valid ? '' : cfResult.error);
                            } else if (newCF.length > 0 && newCF.length < 16) {
                              setCfValidationError('');
                            } else {
                              setCfValidationError('');
                            }
                          }}
                          placeholder="RSSMRA80A01H501U"
                          maxLength={16}
                          className={`uppercase h-11 sm:h-10 ${cfValidationError ? 'border-red-500' : formData.codice_fiscale?.length === 16 && !cfValidationError ? 'border-green-500' : ''}`}
                          data-testid="input-codice-fiscale"
                        />
                        {formData.codice_fiscale && formData.codice_fiscale.length < 16 && (
                          <div className="mt-2 text-xs text-gray-500">
                            {formData.codice_fiscale.length}/16 caratteri
                          </div>
                        )}
                        {cfValidationError && (
                          <div className="mt-2 text-sm text-red-600 flex items-center gap-1">
                            <AlertTriangle className="h-4 w-4" />
                            {t(`checkin.cf_errors.${cfValidationError}`) || t('checkin.cf_errors.generic')}
                          </div>
                        )}
                        {formData.codice_fiscale?.length === 16 && !cfValidationError && (
                          <div className="mt-2 text-sm text-green-600 flex items-center gap-1">
                            <CheckCircle className="h-4 w-4" />
                            {t('checkin.cf_errors.valid')}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Residenza - Collapsible / Optional */}
                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => setShowResidenceFields(!showResidenceFields)}
                      className="w-full flex items-center justify-between p-2 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                      data-testid="toggle-residence-fields"
                    >
                      <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                        <Building className="h-4 w-4" /> {t('checkin.form.residence')}
                        <span className="text-xs font-normal text-slate-400">{t('checkin.form.optional')}</span>
                      </h3>
                      {showResidenceFields ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                    </button>
                    
                    {showResidenceFields && (
                    <div className="space-y-4 pt-2">
                    <div>
                      <Label htmlFor="residence_address">{t('checkin.form.residence_address')} <span className="text-xs text-slate-400">{t('checkin.form.optional')}</span></Label>
                      <Input
                        id="residence_address"
                        value={formData.residence_address}
                        onChange={(e) => setFormData({...formData, residence_address: e.target.value})}
                        placeholder=""
                        data-testid="input-residence-address"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div>
                        <Label htmlFor="residence_city">{t('checkin.form.residence_city')} <span className="text-xs text-slate-400">{t('checkin.form.optional')}</span></Label>
                        {(formData.residence_country === 'Italia' || formData.residence_country?.toLowerCase().includes('ital')) ? (
                          <ComuneAutocomplete
                            id="residence_city"
                            value={formData.residence_city}
                            onChange={(value) => setFormData({...formData, residence_city: value})}
                            onSelect={(comune) => {
                              setFormData({
                                ...formData, 
                                residence_city: comune.nome,
                                residence_province: comune.provincia
                              });
                            }}
                            placeholder={t('checkin.form.search_comune')}
                            allowCustom={true}
                          />
                        ) : (
                          <Input
                            id="residence_city"
                            value={formData.residence_city}
                            onChange={(e) => setFormData({...formData, residence_city: e.target.value})}
                            placeholder=""
                            data-testid="input-residence-city"
                          />
                        )}
                      </div>
                      <div>
                        <Label htmlFor="residence_province">{t('checkin.form.residence_province')} <span className="text-xs text-slate-400">{t('checkin.form.optional')}</span></Label>
                        <Input
                          id="residence_province"
                          value={formData.residence_province}
                          onChange={(e) => setFormData({...formData, residence_province: e.target.value.toUpperCase()})}
                          placeholder="RM"
                          maxLength={2}
                          data-testid="input-residence-province"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div>
                        <Label htmlFor="residence_postal_code">{t('checkin.form.residence_postal_code')} <span className="text-xs text-slate-400">{t('checkin.form.optional')}</span></Label>
                        <Input
                          id="residence_postal_code"
                          value={formData.residence_postal_code}
                          onChange={(e) => setFormData({...formData, residence_postal_code: e.target.value})}
                          placeholder="00100"
                          maxLength={5}
                          data-testid="input-residence-postal-code"
                        />
                      </div>
                      <div>
                        <Label htmlFor="residence_country" className="text-sm">{t('checkin.form.residence_country')} <span className="text-xs text-slate-400">{t('checkin.form.optional')}</span></Label>
                        <SearchableCountrySelect
                          value={formData.residence_country}
                          onChange={(v) => setFormData({...formData, residence_country: v})}
                          placeholder={t('checkin.form.search_country')}
                          testId="select-residence-country"
                        />
                      </div>
                    </div>
                    </div>
                    )}
                  </div>
                  
                  {/* Documento */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      <CreditCard className="h-4 w-4" /> {t('checkin.form.document')}
                    </h3>
                    {!isDocumentRequiredFor(formData) && (
                      <p className="text-xs text-gray-500 -mt-2" data-testid="document-optional-hint">
                        {t('checkin.form.document_not_required_minor')}
                      </p>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div>
                        <Label htmlFor="document_type" className="text-sm">{t('checkin.form.document_type')}{isDocumentRequiredFor(formData) ? ' *' : ''}</Label>
                        <Select 
                          value={formData.document_type} 
                          onValueChange={(v) => setFormData({...formData, document_type: v})}
                        >
                          <SelectTrigger className={getFieldError('document_type')} data-testid="select-document-type">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="id_card">{t('checkin.document_types.id_card')}</SelectItem>
                            <SelectItem value="passport">{t('checkin.document_types.passport')}</SelectItem>
                            <SelectItem value="driving_license">{t('checkin.document_types.driving_license')}</SelectItem>
                            <SelectItem value="other">{t('checkin.document_types.other')}</SelectItem>
                          </SelectContent>
                        </Select>
                        {validationErrors.document_type && <span className="text-xs text-red-500">{t('checkin.form.required_field')}</span>}
                      </div>
                      <div>
                        <Label htmlFor="document_number">{t('checkin.form.document_number')}{isDocumentRequiredFor(formData) ? ' *' : ''}</Label>
                        <Input
                          id="document_number"
                          value={formData.document_number}
                          onChange={(e) => setFormData({...formData, document_number: e.target.value.toUpperCase()})}
                          className={getFieldError('document_number')}
                          placeholder="AA1234567"
                          data-testid="input-document-number"
                        />
                        {validationErrors.document_number && <span className="text-xs text-red-500">{t('checkin.form.required_field')}</span>}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div>
                        <Label htmlFor="document_issuer">{t('checkin.form.document_issuer')}</Label>
                        <Input
                          id="document_issuer"
                          value={formData.document_issuer}
                          onChange={(e) => setFormData({...formData, document_issuer: e.target.value})}
                          placeholder=""
                          data-testid="input-document-issuer"
                        />
                      </div>
                      <div>
                        <Label htmlFor="document_issue_place">{t('checkin.form.document_issue_place')}</Label>
                        <Input
                          id="document_issue_place"
                          value={formData.document_issue_place}
                          onChange={(e) => setFormData({...formData, document_issue_place: e.target.value})}
                          className={getFieldError('document_issue_place')}
                          placeholder=""
                          data-testid="input-document-issue-place"
                        />
                        {validationErrors.document_issue_place && <span className="text-xs text-red-500">{t('checkin.form.required_field')}</span>}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div>
                        <Label htmlFor="document_issue_country" className="text-sm">{t('checkin.form.document_issue_country')}{isDocumentRequiredFor(formData) ? ' *' : ''}</Label>
                        <SearchableCountrySelect
                          value={formData.document_issue_country}
                          onChange={(v) => setFormData({...formData, document_issue_country: v})}
                          placeholder={t('checkin.form.search_country')}
                          className={getFieldError('document_issue_country')}
                          testId="select-document-issue-country"
                        />
                        {validationErrors.document_issue_country && <span className="text-xs text-red-500">{t('checkin.form.required_field')}</span>}
                      </div>
                      <div>
                        <Label htmlFor="document_issue_date" className="text-sm">{t('checkin.form.document_issue_date')}</Label>
                        <Input
                          id="document_issue_date"
                          type="date"
                          value={formData.document_issue_date}
                          onChange={(e) => setFormData({...formData, document_issue_date: e.target.value})}
                          className="h-11 sm:h-10"
                          data-testid="input-document-issue-date"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div>
                        <Label htmlFor="document_expiry_date" className="text-sm">{t('checkin.form.document_expiry_date')}</Label>
                        <Input
                          id="document_expiry_date"
                          type="date"
                          value={formData.document_expiry_date}
                          onChange={(e) => setFormData({...formData, document_expiry_date: e.target.value})}
                          className="h-11 sm:h-10"
                          data-testid="input-document-expiry-date"
                        />
                      </div>
                    </div>
                  </div>

                  {checkinData?.guest_turnover_enabled && (
                    <div className="border-t pt-4 mt-2" data-testid="checkin-turnover-section">
                      <p className="text-sm font-semibold text-amber-800 mb-1">{t('checkin.turnover.title')}</p>
                      <p className="text-xs text-amber-700 mb-3">{t('checkin.turnover.help')}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        <div>
                          <Label htmlFor="arrival_date" className="text-sm">{t('checkin.turnover.arrival')}</Label>
                          <Input
                            id="arrival_date"
                            type="date"
                            value={formData.arrival_date || (checkinData.check_in || '').toString().slice(0, 10)}
                            min={(checkinData.check_in || '').toString().slice(0, 10)}
                            max={(checkinData.check_out || '').toString().slice(0, 10)}
                            onChange={(e) => setFormData({...formData, arrival_date: e.target.value})}
                            className="h-11 sm:h-10"
                            data-testid="input-arrival-date"
                          />
                        </div>
                        <div>
                          <Label htmlFor="departure_date" className="text-sm">{t('checkin.turnover.departure')}</Label>
                          <Input
                            id="departure_date"
                            type="date"
                            value={formData.departure_date || (checkinData.check_out || '').toString().slice(0, 10)}
                            min={(checkinData.check_in || '').toString().slice(0, 10)}
                            max={(checkinData.check_out || '').toString().slice(0, 10)}
                            onChange={(e) => setFormData({...formData, departure_date: e.target.value})}
                            className="h-11 sm:h-10"
                            data-testid="input-departure-date"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Privacy section removed */}
                </CardContent>
                
                {/* Sticky bottom buttons - optimized for mobile */}
                <div className="sticky bottom-0 bg-white border-t p-3 sm:p-4 flex gap-2 sm:gap-3 safe-area-bottom">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1 h-12 sm:h-10 text-sm sm:text-base"
                    onClick={() => setShowForm(false)}
                    data-testid="btn-cancel"
                  >
                    {t('checkin.form.cancel')}
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 h-12 sm:h-10 text-sm sm:text-base bg-blue-600 hover:bg-blue-700 active:bg-blue-800"
                    disabled={submitting}
                    data-testid="btn-save"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        {t('checkin.form.saving')}
                      </>
                    ) : (
                      <>
                        <ChevronRight className="h-4 w-4 mr-2" />
                        {t('checkin.form.continue')}
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </Card>
          </div>
        )}

        {capacityAlert?.is_over_capacity && (
          <div className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4" data-testid="guest-capacity-alert-modal">
            <Card className="w-full max-w-md p-6 border-amber-300">
              <AlertTriangle className="h-10 w-10 text-amber-600 mb-3" />
              <h2 className="text-xl font-bold text-slate-900">Verifica numero ospiti</h2>
              <p className="text-slate-600 mt-2">
                Gli ospiti paganti inseriti ({capacityAlert.registered_paying_guests}) risultano superiori a quelli prenotati ({capacityAlert.booked_paying_guests}). Puoi comunque procedere: l’ufficio Prenotazioni è stato avvisato e ti contatterà se necessario.
              </p>
              {capacityAlert.age_to_verify?.length > 0 && <p className="text-sm text-amber-700 mt-3" data-testid="guest-age-check-notice">Età da verificare: {capacityAlert.age_to_verify.join(', ')}.</p>}
              <Button className="w-full mt-5" onClick={() => setCapacityAlert(null)} data-testid="guest-capacity-alert-confirm">Ho capito, continua</Button>
            </Card>
          </div>
        )}


        {/* Footer */}
        <div className="text-center text-sm text-gray-500 pb-4">
          <p>{t('checkin.footer.company')}</p>
          <p>{t('checkin.footer.security_notice')}</p>
        </div>
      </div>
      
      {/* Document Scanner Modal */}
      {showDocumentScanner && (
        <DocumentScanner
          token={token}
          onDataExtracted={handleOcrDataExtracted}
          onClose={() => setShowDocumentScanner(false)}
        />
      )}
    </div>
  );
}
